/**
 * ONE PERSON THE BAR KNOWS — and, at last, what they pay.
 *
 * The customers list could open an account dialog and edit a row, and that was all: there was no
 * `customers/:customerId` route, unlike staff, suppliers, partners and stock items. So the one
 * question the owner arrived with on 2026-08-11 — "what does this regular pay, and how do I change
 * it?" — had no screen to be asked on, and the capability that answers it (migration 0088, two
 * assistant tools) reached nobody.
 *
 * The arrangement lives here rather than in a dialog on the list because it is a fact ABOUT a person
 * that takes a paragraph to state truthfully — what they pay, what the menu charges, what a
 * promotion can still do to it, and what a partner does not earn on it. A modal over a table is
 * where facts go to be skimmed.
 */
import { Badge, Button, Group, Text } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { rawApi } from '../../api/client';
import { detailKey } from '../../api/queryKeys';
import { RecordEditModal } from '../../components/RecordEditModal';
import { RecordScreen } from '../../components/RecordScreen';
import { enumColor, enumLabel } from '../../lib/enums';
import { notifyDone, notifyError } from '../../lib/notify';
import { formatPhone } from '../../lib/phone';
import { CustomerAccountModal } from './CustomerAccountModal';
import { CUSTOMER_FIELDS, type CustomerRecord } from './customer';
import { type Arrangement, PriceArrangement } from './PriceArrangement';

const CUSTOMER_PATH = '/admin/v1/admin/customers/{id}';
const customerPath = (id: string) => `/admin/v1/admin/customers/${id}`;
const ARRANGEMENT_PATH = '/admin/v1/customers/{id}/price-arrangement';

export function CustomerDetailPage() {
  const { customerId = '' } = useParams();
  const [accountOpen, setAccountOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  /*
   * READ BY ID, not found inside the list. The list is capped at 500 rows and ordered newest-first,
   * so a page that picked its person out of it would announce that the 501st regular is no longer on
   * file — which is the fetch-all-and-filter shape this platform refuses, wearing a detail screen.
   */
  const query = useQuery({
    queryKey: detailKey(CUSTOMER_PATH, customerId),
    queryFn: () => rawApi<CustomerRecord>('GET', customerPath(customerId)),
    enabled: Boolean(customerId),
  });

  /*
   * Read here as well as inside the section, for ONE header fact: whether this person has prices of
   * their own at all. It is the same query key, so it is one request and the section renders from
   * the cache — but the answer belongs beside their phone number, because "do they have a deal?" is
   * a thing to know before scrolling.
   */
  const arrangement = useQuery({
    queryKey: detailKey(ARRANGEMENT_PATH, customerId),
    queryFn: () => rawApi<Arrangement>('GET', `/admin/v1/customers/${customerId}/price-arrangement`),
    enabled: Boolean(customerId),
  });

  const customer = query.data;
  const reach = [formatPhone(customer?.phone), customer?.email].filter(Boolean).join(' · ');
  const visibility = useMutation({
    mutationFn: () =>
      rawApi('PATCH', customerPath(customerId), {
        status: customer?.status === 'retired' ? 'active' : 'retired',
      }),
    onSuccess: () => {
      void query.refetch();
      notifyDone(
        customer?.status === 'retired' ? 'On the register again' : 'Hidden from the register',
        customer?.status === 'retired'
          ? `${customer.name} can be selected on a tab again.`
          : `${customer?.name} remains on file with all history intact.`,
      );
    },
    onError: (error) => notifyError(error, 'change register visibility'),
  });
  // No answer is not the menu price. In particular, a failed read must not turn a network fact into
  // a definitive claim about what this person pays. If React Query has a cached answer, keep using
  // that last known business fact while its refresh reports the failure.
  const whatTheyPay = arrangement.data
    ? arrangement.data.active
      ? 'Their own prices'
      : 'Menu price'
    : arrangement.isError
      ? 'Could not be read'
      : 'Loading…';

  return (
    <>
      {accountOpen && customer && (
        <CustomerAccountModal
          customerId={customer.id}
          name={customer.name}
          onClose={() => setAccountOpen(false)}
        />
      )}
      {customer && (
        <RecordEditModal
          opened={editing}
          onClose={() => {
            setEditing(false);
            void query.refetch();
          }}
          title={`Change ${customer.name}`}
          fields={CUSTOMER_FIELDS}
          record={customer}
          path={customerPath(customer.id)}
          invalidate={[CUSTOMER_PATH]}
          savedLabel={customer.name}
        />
      )}
      <RecordScreen
        backTo="/customers"
        backLabel="All customers"
        isLoading={query.isLoading}
        loadFailed={query.isError && !query.data}
        loadFailedMessage="This person's record could not be read just now. Nothing has changed. Refresh the page to try again."
        notFound={!query.isLoading && !query.isError && !customer}
        notFoundMessage="This person is no longer on file."
        title={customer?.name ?? ''}
        status={customer?.status === 'retired' ? { label: 'Hidden from register', color: 'gray' } : undefined}
        actions={
          customer ? (
            <Group gap="xs">
              <Button variant="default" onClick={() => setEditing(true)}>
                Change details
              </Button>
              <Button variant="light" onClick={() => setAccountOpen(true)}>
                Balance &amp; credit
              </Button>
              <Button
                variant="subtle"
                color={customer.status === 'retired' ? 'teal' : 'gray'}
                loading={visibility.isPending}
                onClick={() => visibility.mutate()}
              >
                {customer.status === 'retired' ? 'Show on register' : 'Hide from register'}
              </Button>
            </Group>
          ) : undefined
        }
        facts={[
          { label: 'How to reach them', value: reach || undefined, empty: 'Nothing on file' },
          {
            label: 'Who they are',
            value:
              customer && customer.linkKind !== 'guest' ? (
                <Badge color={enumColor('customerLinkKind', customer.linkKind)} variant="light">
                  {enumLabel('customerLinkKind', customer.linkKind)}
                </Badge>
              ) : undefined,
            empty: 'A guest',
          },
          {
            label: 'What they pay',
            value: whatTheyPay,
          },
        ]}
        sections={[
          {
            title: 'What they pay',
            description:
              'Prices only this person gets, and one standing discount off everything else. Both apply by themselves the moment a tab is on their name.',
            children: customerId ? <PriceArrangement customerId={customerId} /> : null,
          },
          {
            title: 'Details',
            description:
              'Contact details and who they are are changed here. Employment controls the Bar Staff tag and staff discount; ending employment does not hide the customer.',
            children: (
              <Group gap="xl" wrap="wrap">
                <Detail label="Name on facturas" value={customer?.invoiceName} />
                <Detail label="Notes" value={customer?.notes} />
                <Detail label="Phone" value={formatPhone(customer?.phone)} />
                <Detail label="Email" value={customer?.email} />
              </Group>
            ),
          },
        ]}
      />
    </>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ minWidth: 160 }}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.06em' }}>
        {label}
      </Text>
      <Text size="sm" mt={2}>
        {value || (
          <Text span size="sm" c="dimmed" fs="italic">
            Not recorded
          </Text>
        )}
      </Text>
    </div>
  );
}
